<template>
  <div
    class="gold-card transition--color-bg"
    @click="emitCurrencyItem">
    <div class="gold-card__main">
      <p class="gold-card__title">
        {{ primaryField.length > 0 ? `${primaryField} - ` : '' }}{{ goldAmountFormatted }}
        {{ currencyTitle.replace('gold', 'Gold') }} in stock
      </p>

      <ol class="gold-card__tags">
        <li
          v-for="(tag, index) in tagList"
          :key="index"
          class="gold-card__tag">
          {{ tag }}
        </li>

        <li class="gold-card__tag gold-card__tag--gray">
          {{ minimalPurchase }} Min purchase
        </li>
      </ol>
    </div>

    <currency-seller
      v-if="shouldShowSeller"
      class="gold-card__seller"
      :user="currencyItem.user"
      @click.native="goToSeller" />

    <div class="gold-card__time">
      <span class="gold-card__time-label">Delivery Time</span>
      <span class="gold-card__time-delivery">
        <span class="gold-card__time-icon" /><text-date :value="+currencyItem.guarantee_delivery_time" />
      </span>
    </div>

    <div class="gold-card__price">
      <p class="gold-card__price-main">
        {{ currentPrice }}
      </p>

      <p class="gold-card__price-count">
        For {{ stepSize | transformBigNumber }} {{ currencyTitle }}
      </p>
    </div>

    <button
      class="button gold-card__button button-main--dark-hover"
      type="button">
      <img
        data-src="/images/icons/shield.svg"
        class="lazyload gold-card__button-img"
        width="14"
        height="14"
        alt="">
      Buy Now
    </button>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { transformBigNumber } from 'Root/helpers/tools';
import CurrencySeller from 'Components/Currency/CurrencySeller.vue';
import TextDate from 'Components/TextDate.vue';
import { openSellerPage } from 'Root/helpers/product';

export default {
  name: 'GameOfferCurrencyItem',

  components: { CurrencySeller, TextDate },

  filters: {
    transformBigNumber,
  },

  props: {
    currencyItem: {
      type: Object,
      required: true,
    },

    stepSize: {
      type: Number,
      required: true,
    },

    primaryField: {
      type: String,
      required: false,
      default: '',
    },

    currencyTitle: {
      type: String,
      required: true,
    },

    shouldShowSeller: {
      type: Boolean,
      default: true,
    },
  },

  computed: {
    ...mapState(['activeRegion']),
    ...mapGetters(['calcPriceFromUsd']),

    currentPrice() {
      return this.calcPriceFromUsd(this.activeRegion.id, +this.currencyItem.price_per_step, undefined, 4, true);
    },

    minimalPurchase() {
      return this.currencyItem.min_purchase_amount.toLocaleString('en-US');
    },

    goldAmountFormatted() {
      return this.currencyItem.amount.toLocaleString('en-US');
    },

    tagList() {
      return Object.values(this.currencyItem.data).filter((value) => value !== this.primaryField);
    },
  },

  methods: {
    emitCurrencyItem() {
      this.$emit('select-currency', this.currencyItem);
    },

    goToSeller(event) {
      event.stopPropagation();

      // eslint-disable-next-line camelcase
      const { user_id, product_currency_setting_id } = this.currencyItem;

      openSellerPage(user_id, product_currency_setting_id);
    },
  },
};
</script>

<style lang="scss">
  .gold-card:hover .gold-seller__reviews-count,
  .gold-card:hover .gold-seller__activity {
    color: $color--basic-white;
  }
</style>

<style scoped lang="scss">
  .gold-card {
    background-color: $color--basic-black;
    border: 1px solid $color--basic-menu;
    border-radius: 10px;
    display: grid;
    grid-gap: 16px;
    align-items: center;
    padding: 16px;
    line-height: 1.2;
    font-weight: 500;
    transition-property: background-color, border-color;
    grid-template-areas: "main main"
                         "seller seller"
                         "time time"
                         "price button";

    &:hover {
      background-color: rgba($color--basic-blue, 0.25);
      border-color: $color--basic-blue;

      .gold-card__golds {
        color: currentColor;
      }
    }
  }

  .gold-card__main {
    grid-area: main;
  }

  .gold-card__seller {
    grid-area: seller;
  }

  .gold-card__title {
    font-size: 15px;
    font-weight: 500;
  }

  .gold-card__tags {
    display: flex;
    flex-wrap: wrap;
    margin: 0 -4px 0;
    overflow: hidden;
  }

  .gold-card__tag {
    background-color: $color--basic-blue;
    font-size: 11px;
    padding: 5px 6px 6px;
    border-radius: 4px;
    margin: 8px 4px 0;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .gold-card__tag--gray {
    background-color: $color--basic-dark-gray;
  }

  .gold-card__time {
    position: relative;
    display: flex;
    align-items: center;
    font-size: 13px;
    grid-area: time;
  }

  .gold-card__time-delivery {
    display: flex;
  }

  .gold-card__time-icon {
    mask: url('/images/icons/clock.svg') no-repeat center;
    -webkit-mask: url('/images/icons/clock.svg') no-repeat center;
    mask-size: cover;
    -webkit-mask-size: cover;
    display: inline-block;
    width: 16px;
    height: 16px;
    margin: 0 10px;
    vertical-align: middle;
    background-color: currentColor;
    flex-shrink: 0;
  }

  .gold-card__golds {
    color: $color--basic-dark-gray;
    font-size: 13px;
    margin-top: -8px;
  }

  .gold-card__price {
    grid-area: price;
  }

  .gold-card__price-main {
    --font-size--cent: 0.6em;

    font-size: 17px;
    margin-bottom: 3px;
  }

  .gold-card__price-count {
    font-size: 13px;
  }

  .gold-card__button {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 12px;
    text-transform: none;
    font-size: 13px;
    grid-area: button;
    justify-self: end;
  }

  .gold-card__button-img {
    width: 14px;
    height: 14px;
    margin-right: 10px;
  }

@media only screen and #{$screen-more-or-1024} {
    .gold-card {
      padding-right: 32px;
      grid-gap: 32px;
      grid-auto-flow: column;
      grid-template-columns: 1fr minmax(235px, 0.5fr) minmax(110px, 0.3fr) minmax(110px, 0.2fr) max-content;
      grid-template-areas: "main seller time price button";
    }

    .gold-card__title {
      font-size: 19px;
    }

    .gold-card__time {
      font-size: 15px;
      flex-direction: column;
      align-items: flex-start;
    }

    .gold-card__time-delivery {
      margin-top: 8px;
    }

    .gold-card__time-icon {
      margin-top: 1px;
      margin-left: 0;
    }

    .gold-card__golds {
      display: none;
    }

    .gold-card__golds {
      font-size: 15px;
      min-width: 190px;
      text-align: right;
    }

    .gold-card__price {
      min-width: 70px;
    }

    .gold-card__price-main {
      font-size: 25px;
    }

    .gold-card__price-count {
      font-size: 13px;
    }

    .gold-card__button {
      font-size: 15px;
    }

    .gold-card__button-img {
      width: 20px;
      height: 20px;
    }
  }
</style>
